import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { robustFetch } from "@/utils/robustFetch";

// Helper function to decode HTML entities - most reliable method
const decodeHtmlEntities = (text: string): string => {
  if (!text) return text;

  // Use textarea trick which properly decodes ALL HTML entities
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

interface ScriptData {
  header: string;
  footer: string;
  enabled: boolean;
}

interface ScriptInjections {
  public?: ScriptData;
  user?: ScriptData;
  admin?: ScriptData;
}

export default function ScriptInjectionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const [scripts, setScripts] = useState<ScriptInjections>({});

  // Fetch script injections
  useEffect(() => {
    const fetchScripts = async () => {
      try {
        const response = await robustFetch("/api/script-injections", {
          method: "GET",
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setScripts(data.data);
          }
        }
      } catch (error) {
        // Silently fail - script injection is optional and non-critical
        // Log only in development
        if (process.env.NODE_ENV === "development") {
          console.debug("Failed to fetch script injections:", error);
        }
      }
    };

    fetchScripts();
  }, []);

  // Determine which area the current page belongs to
  const getPageArea = (): "public" | "user" | "admin" | null => {
    const pathname = location.pathname;

    // Admin pages
    if (pathname.startsWith("/admin")) {
      return "admin";
    }

    // User/authenticated pages
    if (
      pathname.startsWith("/dashboard") ||
      pathname.startsWith("/my-files") ||
      pathname.startsWith("/messages")
    ) {
      return "user";
    }

    // Public pages (default)
    return "public";
  };

  const currentArea = getPageArea();
  const currentScripts = currentArea ? scripts[currentArea] : null;

  // Inject footer script into the actual DOM when it changes
  useEffect(() => {
    if (currentScripts?.enabled && currentScripts?.footer?.trim()) {
      try {
        // Decode any HTML entities in the script content
        let decodedFooter = decodeHtmlEntities(currentScripts.footer);

        if (process.env.NODE_ENV === "development") {
          console.debug(
            "Original footer:",
            currentScripts.footer.substring(0, 100),
          );
          console.debug("Decoded footer:", decodedFooter.substring(0, 100));
        }

        // Aggressively decode - handle multiple levels of encoding
        let iterations = 0;
        const maxIterations = 10;

        while (iterations < maxIterations) {
          const beforeDecode = decodedFooter;
          decodedFooter = decodeHtmlEntities(decodedFooter);

          // If no change, we're done
          if (beforeDecode === decodedFooter) {
            break;
          }

          iterations++;

          if (process.env.NODE_ENV === "development") {
            console.debug(
              `Decoding iteration ${iterations}:`,
              decodedFooter.substring(0, 100),
            );
          }
        }

        if (process.env.NODE_ENV === "development") {
          console.debug(
            "Final decoded footer:",
            decodedFooter.substring(0, 150),
          );
          console.debug("Contains <style>:", decodedFooter.includes("<style"));
          console.debug(
            "Contains <script>:",
            decodedFooter.includes("<script"),
          );
        }

        // Handle mixed content (style + script tags)
        // Check if content contains HTML tags like <style> or <script>
        if (
          decodedFooter.includes("<style") ||
          decodedFooter.includes("<script")
        ) {
          // Use innerHTML to handle HTML content properly
          const tempContainer = document.createElement("div");
          tempContainer.innerHTML = decodedFooter;

          if (process.env.NODE_ENV === "development") {
            console.debug(
              "Temp container children:",
              tempContainer.children.length,
            );
            console.debug(
              "Style tags found:",
              tempContainer.querySelectorAll("style").length,
            );
            console.debug(
              "Script tags found:",
              tempContainer.querySelectorAll("script").length,
            );
          }

          // Extract all style tags and append them to head
          const styleTags = tempContainer.querySelectorAll("style");
          styleTags.forEach((styleTag) => {
            try {
              const style = styleTag.cloneNode(true) as HTMLStyleElement;
              style.setAttribute("data-injected", "footer-style");
              document.head.appendChild(style);
              if (process.env.NODE_ENV === "development") {
                console.debug("Style tag injected successfully");
              }
            } catch (error) {
              if (process.env.NODE_ENV === "development") {
                console.error("Failed to append style tag:", error);
              }
            }
          });

          // Extract all script tags and append them to body
          const scriptTags = tempContainer.querySelectorAll("script");
          scriptTags.forEach((scriptTag) => {
            try {
              const script = document.createElement("script");
              script.setAttribute("data-injected", "footer-script");
              script.setAttribute("type", "text/javascript");
              script.textContent = scriptTag.textContent || "";
              document.body.appendChild(script);
              if (process.env.NODE_ENV === "development") {
                console.debug("Script tag injected successfully");
              }
            } catch (error) {
              if (process.env.NODE_ENV === "development") {
                console.error("Failed to append script tag:", error);
              }
            }
          });
        } else {
          // Pure JavaScript content - inject as before
          const script = document.createElement("script");
          script.setAttribute("data-injected", "footer-script");
          script.setAttribute("type", "text/javascript");
          script.textContent = decodedFooter;

          try {
            document.body.appendChild(script);
            if (process.env.NODE_ENV === "development") {
              console.debug("Pure JavaScript injected successfully");
            }
          } catch (error) {
            if (process.env.NODE_ENV === "development") {
              console.error("Failed to append JavaScript:", error);
            }
          }
        }

        return () => {
          // Clean up previously injected footer scripts
          const injectedScripts = document.querySelectorAll(
            'script[data-injected="footer-script"]',
          );
          injectedScripts.forEach((s) => {
            try {
              s.remove();
            } catch (e) {
              // Ignore errors during cleanup
            }
          });

          // Clean up injected styles
          const injectedStyles = document.querySelectorAll(
            'style[data-injected="footer-style"]',
          );
          injectedStyles.forEach((s) => {
            try {
              s.remove();
            } catch (e) {
              // Ignore errors during cleanup
            }
          });
        };
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error preparing footer script injection:", error);
        }
      }
    }
  }, [currentScripts?.footer, currentScripts?.enabled, location.pathname]);

  // Inject header script using Helmet
  useEffect(() => {
    if (!currentScripts?.enabled || !currentScripts?.header?.trim()) {
      return;
    }

    try {
      let decodedHeader = decodeHtmlEntities(currentScripts.header);

      // Aggressively clean up any remaining HTML entities
      let previousValue = "";
      let iterations = 0;
      while (
        decodedHeader !== previousValue &&
        decodedHeader.includes("&") &&
        iterations < 5
      ) {
        previousValue = decodedHeader;
        decodedHeader = decodeHtmlEntities(decodedHeader);
        iterations++;
      }

      // Final cleanup
      if (decodedHeader.includes("&lt;") || decodedHeader.includes("&gt;")) {
        decodedHeader = decodedHeader
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&amp;/g, "&")
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&apos;/g, "'");
      }

      if (process.env.NODE_ENV === "development") {
        console.debug("Decoded header:", decodedHeader.substring(0, 150));
      }

      // Handle mixed content (style + script tags) in header
      if (
        decodedHeader.includes("<style") ||
        decodedHeader.includes("<script")
      ) {
        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = decodedHeader;

        // Extract and inject style tags to head
        const styleTags = tempContainer.querySelectorAll("style");
        styleTags.forEach((styleTag) => {
          try {
            const style = styleTag.cloneNode(true) as HTMLStyleElement;
            style.setAttribute("data-injected", "header-style");
            document.head.appendChild(style);
            if (process.env.NODE_ENV === "development") {
              console.debug("Header style tag injected");
            }
          } catch (error) {
            if (process.env.NODE_ENV === "development") {
              console.error("Failed to append header style tag:", error);
            }
          }
        });

        // Extract and inject script tags to head
        const scriptTags = tempContainer.querySelectorAll("script");
        scriptTags.forEach((scriptTag) => {
          try {
            const script = document.createElement("script");
            script.setAttribute("data-injected", "header-script");
            script.setAttribute("type", "text/javascript");
            script.textContent = scriptTag.textContent || "";
            document.head.appendChild(script);
            if (process.env.NODE_ENV === "development") {
              console.debug("Header script tag injected");
            }
          } catch (error) {
            if (process.env.NODE_ENV === "development") {
              console.error("Failed to append header script tag:", error);
            }
          }
        });
      } else {
        // Pure JavaScript in header - inject via Helmet
        // (Helmet will handle script injection to head)
      }

      return () => {
        // Clean up injected header scripts
        const injectedScripts = document.querySelectorAll(
          'script[data-injected="header-script"]',
        );
        injectedScripts.forEach((s) => {
          try {
            s.remove();
          } catch (e) {
            // Ignore errors during cleanup
          }
        });

        // Clean up injected header styles
        const injectedStyles = document.querySelectorAll(
          'style[data-injected="header-style"]',
        );
        injectedStyles.forEach((s) => {
          try {
            s.remove();
          } catch (e) {
            // Ignore errors during cleanup
          }
        });
      };
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error("Error preparing header script injection:", error);
      }
    }
  }, [currentScripts?.enabled, currentScripts?.header, location.pathname]);

  return (
    <>
      <Helmet>
        {currentScripts?.enabled &&
          currentScripts?.header?.trim() &&
          !currentScripts.header.includes("<script") &&
          !currentScripts.header.includes("<style") && (
            <script
              dangerouslySetInnerHTML={{
                __html: decodeHtmlEntities(currentScripts.header),
              }}
              suppressHydrationWarning
            />
          )}
      </Helmet>
      {children}
    </>
  );
}
